import { createTheme } from "@mui/material/styles";

// Light theme configuration
export const lightTheme = createTheme({
    palette: {
        mode: "light",
        primary: {
            main: "#00a896",
            contrastText: "#ffffff",
        },
        secondary: {
            main: "#2c3e50",
        },
        background: {
            default: "#f5f7fa",
            paper: "#ffffff",
        },
        text: {
            primary: "#2c3e50",
            secondary: "#546e7a",
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
                    border: "1px solid rgba(0,0,0,0.05)",
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "none",
                    "&:hover": {
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: "#ffffff",
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#b0bec5",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a896",
                        borderWidth: 2,
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: "#ffffff",
                    color: "#2c3e50",
                    boxShadow: "0 1px 0 rgba(0,0,0,0.05)",
                },
            },
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h5: { fontWeight: 600, color: "#1a202c" },
        h6: { fontWeight: 600, color: "#2d3748" },
        subtitle1: { color: "#4a5568" },
        body1: { color: "#2d3748" },
    },
});

// Dark theme configuration (Zinc Palette)
export const darkTheme = createTheme({
    palette: {
        mode: "dark",
        primary: {
            main: "#2dd4bf", // Teal-400 (Brighter for dark mode)
            contrastText: "#000000",
        },
        secondary: {
            main: "#a1a1aa", // Zinc-400
        },
        background: {
            default: "#09090b", // Zinc-950 (Deepest background)
            paper: "#18181b",   // Zinc-900 (Card background)
        },
        text: {
            primary: "#f4f4f5", // Zinc-100
            secondary: "#a1a1aa", // Zinc-400
        },
        divider: "rgba(255, 255, 255, 0.08)",
        action: {
            hover: "rgba(255, 255, 255, 0.04)",
            selected: "rgba(255, 255, 255, 0.08)",
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                    border: "1px solid rgba(255, 255, 255, 0.08)", // Subtle border
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 600,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.15)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#2dd4bf",
                        borderWidth: 2,
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: "#09090b",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: "#18181b",
                    borderTop: "1px solid rgba(255, 255, 255, 0.08)",
                }
            }
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    scrollbarColor: "#3f3f46 #09090b",
                    "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
                        width: "10px",
                        height: "10px",
                        backgroundColor: "#09090b",
                    },
                    "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                        borderRadius: "8px",
                        backgroundColor: "#3f3f46",
                        minHeight: "24px",
                        border: "2px solid #09090b",
                    },
                    "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
                        backgroundColor: "#52525b",
                    },
                    "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active": {
                        backgroundColor: "#52525b",
                    },
                    "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#52525b",
                    },
                    "&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
                        backgroundColor: "#09090b",
                    },
                },
            },
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h5: { fontWeight: 600, color: "#f4f4f5" },
        h6: { fontWeight: 600, color: "#e4e4e7" },
        subtitle1: { color: "#a1a1aa" },
        subtitle2: { color: "#a1a1aa", letterSpacing: '0.05em', fontWeight: 600 },
        body1: { color: "#e4e4e7" },
        body2: { color: "#d4d4d8" },
    },
});
